import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, AlertCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface AppSettings {
  bkash_send_money_number: string;
  bkash_charge_rate: number;
  sms_min_qty: number;
  sms_unit_price: number;
}

export default function SmsBuy() {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [quantity, setQuantity] = useState<number>(1000);
  const [payerNumber, setPayerNumber] = useState('');
  const [trxId, setTrxId] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadSettings();
    generateReferenceCode();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);

      const { data: settingsData, error: settingsError } = await supabase
        .from('app_settings')
        .select('*')
        .in('key', ['bkash_send_money_number', 'bkash_charge_rate', 'sms_min_qty', 'sms_unit_price']);

      if (settingsError) {
        console.error('Settings query error:', settingsError);
        alert(`Failed to load app settings: ${settingsError.message || 'Unknown error'}`);
        return;
      }

      const raw: any = {};
      settingsData?.forEach(({ key, value }) => {
        raw[key] = value;
      });

      const normalized = {
        bkash_send_money_number: String(raw.bkash_send_money_number ?? '01681354066'),
        bkash_charge_rate: Number(raw.bkash_charge_rate ?? 0.015),
        sms_min_qty: Number(raw.sms_min_qty ?? 1000),
        sms_unit_price: Number(raw.sms_unit_price ?? 0.30),
      };

      setSettings(normalized);
      setQuantity(normalized.sms_min_qty);
    } catch (error: any) {
      console.error('Failed to load settings:', error);
      alert(`Failed to load SMS purchase settings: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const generateReferenceCode = async () => {
    try {
      const { data, error } = await supabase.rpc('generate_reference_code');
      if (error) throw error;
      setReferenceCode(data);
    } catch (error) {
      console.error('Failed to generate reference code:', error);
      const timestamp = Date.now().toString(36);
      const random = Math.random().toString(36).substring(2, 8).toUpperCase();
      setReferenceCode(`SB-${timestamp}-${random}`);
    }
  };

  const calculateAmount = () => {
    if (!settings) return { amount: 0, charge: 0, total: 0 };
    
    const unitPrice = settings.sms_unit_price || 0.30;
    const chargeRate = settings.bkash_charge_rate || 0.015;
    
    const amount = quantity * unitPrice;
    const charge = amount * chargeRate;
    const total = amount + charge;
    
    return { amount, charge, total };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!settings) {
      alert('Settings not loaded');
      return;
    }

    const minQty = settings.sms_min_qty || 1000;
    if (quantity < minQty) {
      alert(`Minimum order quantity is ${minQty} SMS`);
      return;
    }

    if (!payerNumber) {
      alert('Please enter your bKash number');
      return;
    }

    if (payerNumber.length !== 11) {
      alert('Please enter a valid 11-digit bKash number');
      return;
    }

    try {
      setSubmitting(true);

      const { amount, charge, total } = calculateAmount();
      const bkashNumber = settings.bkash_send_money_number || '01681354066';
      const unitPrice = settings.sms_unit_price || 0.30;
      const chargeRate = settings.bkash_charge_rate || 0.015;

      const { data: paymentData, error: paymentError } = await supabase
        .from('software_payments')
        .insert({
          type: 'sms',
          amount: amount,
          charge_amount: charge,
          total_amount: total,
          payment_method: 'bkash',
          channel: 'send_money',
          bkash_send_number: bkashNumber,
          payer_number: payerNumber,
          reference_code: referenceCode,
          trx_id: trxId || null,
          status: 'pending',
        })
        .select()
        .single();

      if (paymentError) {
        console.error('Payment insert error:', paymentError);
        alert(`Failed to create payment: ${paymentError.message || 'Unknown error'}`);
        return;
      }

      const { error: smsError } = await supabase
        .from('sms_purchases')
        .insert({
          quantity: quantity,
          unit_price: unitPrice,
          amount: amount,
          charge_rate: chargeRate,
          charge_amount: charge,
          total_amount: total,
          payment_id: paymentData.id,
          status: 'pending',
        });

      if (smsError) {
        console.error('SMS purchase insert error:', smsError);
        alert(`Failed to create SMS purchase: ${smsError.message || 'Unknown error'}`);
        return;
      }

      alert('SMS purchase request submitted successfully! Your payment is pending approval.');
      navigate('/softwarebilling/smshistory');
    } catch (error: any) {
      console.error('Failed to submit SMS purchase:', error);
      alert(`Failed to submit SMS purchase request: ${error?.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading SMS purchase details...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Failed to load SMS purchase settings</div>
      </div>
    );
  }

  const { amount, charge, total } = calculateAmount();
  const bkashNumber = settings.bkash_send_money_number || '01681354066';
  const minQty = settings.sms_min_qty || 1000;
  const chargeRate = settings.bkash_charge_rate || 0.015;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">SMS Purchase</h1>
        <p className="text-gray-600 mt-1">Buy SMS credits via bKash payment</p>
      </div>

      {/* bKash Payment Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-center mb-6">
          <img 
            src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 60'%3E%3Ctext x='10' y='40' font-family='Arial' font-size='32' font-weight='bold' fill='%23E2136E'%3EbKash%3C/text%3E%3Ctext x='120' y='40' font-family='Arial' font-size='20' fill='%23E2136E'%3EPayment%3C/text%3E%3C/svg%3E" 
            alt="bKash Payment" 
            className="h-16"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* SMS Quantity Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter SMS QTY. <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
              min={minQty}
              step={100}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-red-500 mt-1">
              Non Mask 09617, minimum order quantity {minQty} SMS
            </p>
          </div>

          {/* Amount Calculation */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Amount:</span>
              <span className="text-lg font-semibold text-gray-900">
                {formatCurrency(amount)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">bKash Charge: {(chargeRate * 100).toFixed(1)}%</span>
              <span className="text-lg font-semibold text-gray-900">
                {formatCurrency(charge)}
              </span>
            </div>
            <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
              <span className="text-base font-medium text-gray-900">Grand Total:</span>
              <span className="text-2xl font-bold text-primary-600">
                {formatCurrency(total)}
              </span>
            </div>
          </div>

          {/* bKash Instructions */}
          <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
            <div className="text-sm font-medium text-pink-900 mb-2">
              bKash Send Money: {bkashNumber}
            </div>
            <div className="text-sm text-pink-800">
              Reference: {referenceCode}
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Payment Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open your bKash app</li>
                  <li>Select "Send Money"</li>
                  <li>Enter the number: {bkashNumber}</li>
                  <li>Enter amount: {formatCurrency(total)}</li>
                  <li>Complete the payment</li>
                  <li>Enter your bKash number and transaction ID below</li>
                </ol>
              </div>
            </div>
          </div>

          {/* bKash Number Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              bKash Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={payerNumber}
              onChange={(e) => setPayerNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="01XXXXXXXXX"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              maxLength={11}
            />
            <p className="text-xs text-gray-500 mt-1">Enter the bKash number you used for payment</p>
          </div>

          {/* Transaction ID Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction ID (Optional)
            </label>
            <input
              type="text"
              value={trxId}
              onChange={(e) => setTrxId(e.target.value)}
              placeholder="Enter bKash transaction ID"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center justify-center"
          >
            <MessageSquare className="h-5 w-5 mr-2" />
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
}
